import { getDomain } from '@/helpers/getDomains';
import { type ErrorResponse } from '@/types';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { buildScribeSseHeaders } from '@/utils/http/scribeSseHeaders';
import { type EventSourceMessage, fetchEventSource } from '@fortaine/fetch-event-source';

type Params = {
  requestBody?: object | null;
  path: string;
  /** Gets the HTTP status. Only 400/403 reject; others stream. */
  onopen?: (status: number) => void;
  onmessage?: (ev: EventSourceMessage) => void;
  onclose?: () => void;
  onAbort?: () => void;
  // onerror?: (error: string) => void;
  endPointVersion?: 'v1' | 'v2' | 'v2-1';
  abortController: AbortController;
};

/** The error a 400 or 403 stream rejects with. */
export type StreamHttpError = Error & { status: number };

// Message is the backend `detail.type`; a non-JSON body (e.g. a WAF page)
// rejects as `StreamHttpError`.
const readStreamHttpError = async (response: Response): Promise<StreamHttpError> => {
  let detail: Partial<ErrorResponse['detail']> | undefined;
  try {
    const parsed = JSON.parse(await response.text()) as { detail?: unknown };
    if (parsed.detail && typeof parsed.detail === 'object') {
      detail = parsed.detail;
    }
  } catch {
    detail = undefined;
  }
  const type = typeof detail?.type === 'string' ? detail.type : 'StreamHttpError';
  return Object.assign(new Error(type, { cause: detail?.msg }), {
    status: response.status,
  });
};

export const customFetchEventSource = (param: Params) => {
  const endPointVersion = param.endPointVersion || 'v2';
  const sessionURL = getDomain(endPointVersion) + param.path;
  const token = getAuthTokenForHttpClientSync();

  const requestHeaders: Record<string, string> = {
    ...buildScribeSseHeaders(token),
  };

  //   const requestBody:

  let transformedChar = '';

  const ctrl = param.abortController;

  //   wrap it in promise
  const promise = new Promise<string>((resolve, reject) => {
    const abortHandler = () => {
      reject(new Error('StreamManuallyAbortedError'));
      param.onAbort?.();
    };
    ctrl.signal.addEventListener('abort', abortHandler);

    void fetchEventSource(sessionURL, {
      headers: requestHeaders,
      body: JSON.stringify(param.requestBody),
      method: 'POST',
      openWhenHidden: true,
      signal: ctrl.signal,

      onopen: async (response) => {
        if (response.status === 400 || response.status === 403) {
          // Throw, not abort: `onerror` then rejects and aborts. An abort here
          // would let the library carry on into `onclose`.
          throw await readStreamHttpError(response);
        }
        param.onopen?.(response.status);
      },
      onmessage: (ev) => {
        const data = ev.data;
        const currentString = data === '' ? '\n' : data.replace(/ {3}/g, '\n');

        setTimeout(() => {
          if (endPointVersion === 'v2-1' || param.path.includes('live-dictation')) {
            const jsonData = JSON.parse(data) as { data: string };
            transformedChar += jsonData.data;

            param.onmessage?.({
              ...ev,
              data: jsonData.data,
            });
          } else {
            transformedChar += currentString;
            param.onmessage?.(ev);
          }
        }, 0);
      },
      onclose: () => {
        ctrl.signal.removeEventListener('abort', abortHandler);
        resolve(transformedChar);
        param.onclose?.();
        ctrl?.abort();
      },
      onerror: (error) => {
        ctrl.signal.removeEventListener('abort', abortHandler);
        reject(error instanceof Error ? error : new Error(String(error)));
        ctrl?.abort();
        throw error;
      },
    });
  });

  return promise;
};
